import React from 'react';
import {
  SparklesIcon,
  TicketIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  WrenchScrewdriverIcon,
  ArrowTrendingUpIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentCheckIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  ClockIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  PaperClipIcon,
  PhoneIcon,
  Bars3BottomLeftIcon,
  UserIcon,
  ChartBarIcon,
  UsersIcon,
  AtSymbolIcon,
} from '@heroicons/react/24/outline';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/core/auth/AuthContext';
import './WhatsNewTab.css';

interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  color: string;
}

interface ReleaseNote {
  version: string;
  date: string;
  isNew?: boolean;
  features: {
    title: string;
    description: string;
    icon: React.ReactNode;
    action?: {
      label: string;
      path: string;
    };
  }[];
  fixes?: string[];
  improvements?: string[];
}

const releaseNotes: ReleaseNote[] = [
  {
    version: '2.9.0',
    date: 'Décembre 2025',
    isNew: true,
    features: [
      {
        title: 'Calendrier Équipe',
        description:
          'Visualisez les disponibilités de toute l\'équipe sur un calendrier partagé. Voyez qui est présent, absent ou en rendez-vous d\'un coup d\'œil.',
        icon: <UsersIcon />,
        action: {
          label: 'Voir l\'équipe',
          path: '/admin/equipe',
        },
      },
      {
        title: 'Activité Bureau',
        description:
          'Nouveau tableau de bord pour suivre l\'activité du bureau : statistiques, graphiques de performance, et suivi en temps réel des utilisateurs connectés.',
        icon: <ChartBarIcon />,
        action: {
          label: 'Voir l\'activité',
          path: '/admin/bureau-activity',
        },
      },
      {
        title: 'Badges de tâches améliorés',
        description:
          'Les tâches affichent maintenant deux badges séparés : le statut (A faire, En cours...) et la priorité (Urgent, Haute...). Cliquez sur un badge pour le modifier directement.',
        icon: <ClipboardDocumentCheckIcon />,
        action: {
          label: 'Voir mes tâches',
          path: '/admin/tasks',
        },
      },
      {
        title: 'Mentions dans les tickets',
        description:
          'Mentionnez vos collègues avec @ dans les commentaires de tickets. Ils recevront une notification pour ne rien manquer.',
        icon: <AtSymbolIcon />,
        action: {
          label: 'Voir les tickets',
          path: '/admin/ticket',
        },
      },
      {
        title: 'Vue chronologique Tracker',
        description:
          'Nouvelle vue chronologique complète pour chaque dossier : emails, appels, rendez-vous, documents et communications internes sur une timeline.',
        icon: <ClockIcon />,
        action: {
          label: 'Voir le Tracker',
          path: '/admin/tracker',
        },
      },
    ],
    improvements: [
      'Filtres par groupes de statuts dans le Tracker (Ouvert, Terminé, En attente)',
      'Indicateur visuel pour les tâches en retard (badge qui pulse en rouge)',
      'Menus déroulants des badges qui ne sont plus masqués par les autres tâches',
      'Changement de statut et priorité en un clic',
      'Performance améliorée des listes de tâches',
    ],
    fixes: [
      'Correction du positionnement des menus déroulants',
      'Correction des erreurs de mise à jour de statut',
      'Amélioration de la synchronisation des emails',
    ],
  },
  {
    version: '2.8.0',
    date: 'Décembre 2025',
    features: [
      {
        title: 'Réunion du lundi',
        description:
          'Suivez les actions décidées lors des réunions du lundi. Assignez des tâches à vos collègues, définissez une échéance par semaine (S49, S50...), et visualisez les tâches en retard en rouge.',
        icon: <CalendarDaysIcon />,
        action: {
          label: 'Voir les actions',
          path: '/admin/monday-meeting',
        },
      },
    ],
    improvements: [
      'Filtrez par réunion pour voir toutes les actions discutées lors d\'une semaine spécifique',
      'Filtrez par échéance pour voir les tâches à traiter pour une semaine donnée',
      'Les tâches en retard s\'affichent en premier automatiquement',
      'Ajoutez plusieurs assignés par tâche',
      'Commentez les tâches pour suivre l\'avancement',
    ],
  },
  {
    version: '2.7.0',
    date: 'Décembre 2025',
    features: [
      {
        title: 'Planning étendu jusqu\'à minuit',
        description:
          'Vous pouvez maintenant planifier vos rendez-vous jusqu\'à minuit (00h). Idéal pour les expertises en soirée ou les disponibilités tardives.',
        icon: <CalendarDaysIcon />,
        action: {
          label: 'Voir mon planning',
          path: '/admin/mon-espace',
        },
      },
      {
        title: 'Mon Espace amélioré',
        description:
          'La section "Informations perso" affiche maintenant un bouton de sauvegarde permanent. Plus besoin de chercher comment enregistrer vos modifications.',
        icon: <UserIcon />,
        action: {
          label: 'Mes informations',
          path: '/admin/mon-espace',
        },
      },
    ],
    improvements: [
      'Appuyez sur Entrée pour valider rapidement les horaires dans le planning',
      'Cliquez en dehors du calendrier pour annuler une sélection',
      'Interface planning plus intuitive',
    ],
  },
  {
    version: '2.6.0',
    date: 'Décembre 2025',
    features: [
      {
        title: 'Système de Ticketing',
        description:
          'Besoin d\'aide d\'un collègue ? Créez un ticket en quelques clics, ajoutez des documents si nécessaire, et suivez l\'avancement de votre demande en temps réel.',
        icon: <TicketIcon />,
        action: {
          label: 'Créer un ticket',
          path: '/admin/ticket',
        },
      },
      {
        title: 'Menu simplifié',
        description:
          'Le menu latéral est maintenant organisé par catégories. Cliquez sur un groupe pour voir les options, et retrouvez vos outils plus rapidement.',
        icon: <Bars3BottomLeftIcon />,
      },
    ],
    fixes: [
      'Les filtres fonctionnent correctement',
      'Les notifications s\'affichent bien',
    ],
    improvements: [
      'Tableau de bord plus lisible',
      'Chargement plus rapide',
    ],
  },
  {
    version: '2.5.0',
    date: 'Décembre 2025',
    features: [
      {
        title: 'Emails → Dossiers',
        description:
          'Un email client arrive ? Transformez-le directement en tâche ou rattachez-le à un dossier existant. Fini les copier-coller !',
        icon: <EnvelopeIcon />,
        action: {
          label: 'Voir mes emails',
          path: '/admin/emails',
        },
      },
    ],
    fixes: [
      'Tri des tâches corrigé',
      'Documents joints bien sauvegardés',
    ],
    improvements: [
      'Traitement des emails plus intuitif',
      'Ajout de documents simplifié',
    ],
  },
  {
    version: '2.4.0',
    date: 'Novembre 2025',
    features: [
      {
        title: 'Messages vocaux',
        description:
          'Les messages vocaux de vos clients sont maintenant visibles dans l\'application. Écoutez-les et assignez-les au bon dossier.',
        icon: <PhoneIcon />,
      },
      {
        title: 'Tous les documents d\'un dossier',
        description:
          'Un nouvel onglet "Documents" vous permet de voir d\'un coup d\'œil toutes les pièces jointes liées à un dossier.',
        icon: <PaperClipIcon />,
      },
    ],
    improvements: [
      'Vue pipeline redessinée',
      'Filtres plus précis',
      'Historique plus clair',
    ],
  },
  {
    version: '2.3.0',
    date: 'Novembre 2025',
    features: [
      {
        title: 'Paiement groupé',
        description: 'Réglez plusieurs dossiers en une seule fois. Idéal pour les clients avec plusieurs expertises en cours.',
        icon: <CreditCardIcon />,
      },
    ],
    improvements: [
      'Application plus rapide',
      'Interface plus claire',
    ],
  },
  {
    version: '2.2.0',
    date: 'Novembre 2025',
    features: [
      {
        title: 'Intelligence Artificielle',
        description:
          'Générez automatiquement des appréciations pour vos dossiers grâce à l\'IA. Personnalisez les prompts et réutilisez l\'historique des générations.',
        icon: <SparklesIcon />,
        action: {
          label: 'Voir l\'IA',
          path: '/admin/ia',
        },
      },
      {
        title: 'Tracker de dossiers',
        description:
          'Suivez toutes les communications d\'un dossier en un seul endroit : emails, appels, rendez-vous, documents. Ne perdez plus le fil des échanges.',
        icon: <ClipboardDocumentCheckIcon />,
        action: {
          label: 'Voir le Tracker',
          path: '/admin/tracker',
        },
      },
    ],
    improvements: [
      'Synchronisation des emails depuis FileMaker',
      'Assignation des messages vocaux aux dossiers',
      'Historique des communications internes',
    ],
  },
  {
    version: '2.1.0',
    date: 'Octobre 2025',
    features: [
      {
        title: 'Automatisations',
        description:
          'Créez des règles automatiques pour vos tâches. Définissez des déclencheurs, des actions récurrentes et suivez l\'historique d\'exécution.',
        icon: <ClockIcon />,
        action: {
          label: 'Voir les automatisations',
          path: '/admin/automatisation',
        },
      },
      {
        title: 'Notifications en temps réel',
        description:
          'Recevez instantanément les nouvelles tâches et mises à jour grâce aux WebSockets. Plus besoin de rafraîchir la page.',
        icon: <ChatBubbleLeftRightIcon />,
      },
    ],
    improvements: [
      'Indicateurs de tâches non lues',
      'Son de notification configurable dans les paramètres',
      'Filtrage avancé des tâches par dossier',
    ],
  },
  {
    version: '2.0.0',
    date: 'Septembre 2025',
    features: [
      {
        title: 'Gestion des Experts',
        description:
          'Nouveau tableau de bord pour gérer vos experts : voir leurs dossiers en cours, leur disponibilité et leurs statistiques.',
        icon: <UserIcon />,
        action: {
          label: 'Voir les experts',
          path: '/admin/expert',
        },
      },
      {
        title: 'Pipeline visuel',
        description:
          'Visualisez le parcours de vos dossiers à travers 11 étapes : de la réception à la transmission. Glissez-déposez pour changer d\'étape.',
        icon: <ArrowTrendingUpIcon />,
        action: {
          label: 'Voir le Pipeline',
          path: '/admin/pipeline',
        },
      },
      {
        title: 'Recherche avancée',
        description:
          'Recherchez vos clients et dossiers par nom, adresse, numéro d\'affaire ou période. Trouvez instantanément ce que vous cherchez.',
        icon: <MagnifyingGlassIcon />,
        action: {
          label: 'Rechercher',
          path: '/admin/search',
        },
      },
    ],
    improvements: [
      'Nouveau rôle "Banque" pour le suivi des paiements',
      'Nouveau rôle "Client" avec accès aux rapports',
      'Interface expert avec planning et préférences',
      'Synchronisation bidirectionnelle avec FileMaker',
    ],
  },
  {
    version: '1.0.0',
    date: 'Août 2025',
    features: [
      {
        title: 'Mes Tâches',
        description:
          'Gérez toutes vos tâches en un seul endroit. Créez, assignez, commentez et suivez l\'avancement de vos actions.',
        icon: <CheckCircleIcon />,
        action: {
          label: 'Voir mes tâches',
          path: '/admin/tasks',
        },
      },
      {
        title: 'Planning Expert',
        description:
          'Les experts peuvent définir leur disponibilité sur un calendrier, choisir leurs zones d\'intervention et marquer leurs absences.',
        icon: <CalendarDaysIcon />,
      },
      {
        title: 'Tableau de bord par rôle',
        description:
          'Chaque utilisateur (Admin, Bureau, Expert) dispose de son propre tableau de bord adapté à ses besoins.',
        icon: <UserIcon />,
      },
    ],
    improvements: [
      'Authentification sécurisée avec JWT',
      'Support multilingue (FR, EN, NL, DE)',
      'Thème clair/sombre',
      'Application responsive',
    ],
  },
];

export const WhatsNewTab: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const basePath = location.pathname.startsWith('/bureau') ? '/bureau' : '/admin';

  const quickActions: QuickAction[] = [
    {
      id: 'search',
      title: 'Rechercher',
      description: 'Clients & Dossiers',
      icon: <MagnifyingGlassIcon />,
      path: `${basePath}/search`,
      color: '#3b82f6',
    },
    {
      id: 'tracker',
      title: 'Tracker',
      description: 'Suivi des affaires',
      icon: <ClipboardDocumentCheckIcon />,
      path: `${basePath}/tracker`,
      color: '#8b5cf6',
    },
    {
      id: 'tasks',
      title: 'Mes Tâches',
      description: 'À traiter',
      icon: <CalendarDaysIcon />,
      path: `${basePath}/tasks`,
      color: '#10b981',
    },
    {
      id: 'ticket',
      title: 'Ticket',
      description: 'Ticketing & plus',
      icon: <TicketIcon />,
      path: `${basePath}/ticket`,
      color: '#ef5343',
    },
    {
      id: 'monday-meeting',
      title: 'Réunion du lundi',
      description: 'Suivi des actions',
      icon: <CalendarDaysIcon />,
      path: `${basePath}/monday-meeting`,
      color: '#7c3aed',
    },
    {
      id: 'equipe',
      title: 'Équipe',
      description: 'Calendrier partagé',
      icon: <UsersIcon />,
      path: `${basePath}/equipe`,
      color: '#0ea5e9',
    },
  ];

  const handleAction = (path: string) => {
    navigate(path);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bonjour';
    if (hour < 18) return 'Bon après-midi';
    return 'Bonsoir';
  };

  return (
    <div className="wn-tab">
      {/* Welcome Hero */}
      <div className="wn-hero">
        <div className="wn-hero-content">
          <h1>{getGreeting()}, {user?.firstName || 'Utilisateur'}</h1>
          <p>Bienvenue sur votre espace de travail</p>
        </div>
        <div className="wn-hero-decoration">
          <div className="wn-hero-circle wn-hero-circle-1"></div>
          <div className="wn-hero-circle wn-hero-circle-2"></div>
          <div className="wn-hero-circle wn-hero-circle-3"></div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="wn-quick-actions">
        {quickActions.map((action) => (
          <button
            key={action.id}
            className="wn-action-card"
            onClick={() => handleAction(action.path)}
          >
            <div
              className="wn-action-icon"
              style={{ backgroundColor: `${action.color}15`, color: action.color }}
            >
              {action.icon}
            </div>
            <div className="wn-action-info">
              <span className="wn-action-title">{action.title}</span>
              <span className="wn-action-desc">{action.description}</span>
            </div>
            <ArrowRightIcon className="wn-action-arrow" />
          </button>
        ))}
      </div>

      {/* Featured Updates - Show all features from isNew releases */}
      <div className="wn-featured-grid">
        {releaseNotes
          .filter(release => release.isNew)
          .flatMap(release =>
            release.features.map((feature, idx) => ({
              ...feature,
              version: release.version,
              key: `${release.version}-${idx}`
            }))
          )
          .map((feature) => (
            <div key={feature.key} className="wn-featured">
              <div className="wn-featured-header">
                <div className="wn-featured-label">
                  <SparklesIcon />
                  <span>Nouveau</span>
                </div>
                <span className="wn-featured-version">v{feature.version}</span>
              </div>
              <div className="wn-featured-body">
                <div className="wn-featured-icon">
                  {feature.icon}
                </div>
                <div className="wn-featured-info">
                  <h2>{feature.title}</h2>
                  <p>{feature.description}</p>
                  {feature.action && (
                    <button
                      className="wn-featured-btn"
                      onClick={() => handleAction(feature.action!.path)}
                    >
                      {feature.action.label}
                      <ArrowRightIcon />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* Reminder */}
      <div className="wn-reminder-banner">
        <SparklesIcon />
        <span>N'oubliez pas de compléter votre planning dans Mon Espace pour une meilleure organisation !</span>
      </div>

      {/* Release Timeline */}
      <div className="wn-timeline">
        <div className="wn-timeline-card">
          <div className="wn-timeline-card-header">
            <ClockIcon className="wn-timeline-header-icon" />
            <h3>Historique des mises à jour</h3>
          </div>

          <div className="wn-timeline-container">
            {releaseNotes.map((release, index) => (
              <div key={release.version} className={`wn-timeline-item ${release.isNew ? 'wn-current' : ''}`}>
                <div className="wn-timeline-marker">
                  <div className="wn-timeline-dot"></div>
                  {index < releaseNotes.length - 1 && <div className="wn-timeline-line"></div>}
                </div>

                <div className="wn-timeline-content">
                  <div className="wn-timeline-header">
                    <div className="wn-timeline-version">
                      <span className="wn-version-tag">v{release.version}</span>
                      {release.isNew && <span className="wn-current-badge">Actuel</span>}
                    </div>
                    <span className="wn-timeline-date">{release.date}</span>
                  </div>

                  <div className="wn-timeline-body">
                    {/* Features (skip all for isNew releases as they're featured above) */}
                    {!release.isNew && release.features.map((feature, idx) => (
                      <div key={idx} className="wn-timeline-feature">
                        <div className="wn-timeline-feature-icon">{feature.icon}</div>
                        <div>
                          <strong>{feature.title}</strong>
                          <span>{feature.description}</span>
                        </div>
                      </div>
                    ))}

                    {/* Improvements */}
                    {release.improvements && release.improvements.length > 0 && (
                      <div className="wn-timeline-section">
                        <div className="wn-timeline-section-header">
                          <ArrowTrendingUpIcon />
                          <span>Améliorations</span>
                        </div>
                        <ul className="wn-timeline-list wn-improvements">
                          {release.improvements.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Fixes */}
                    {release.fixes && release.fixes.length > 0 && (
                      <div className="wn-timeline-section">
                        <div className="wn-timeline-section-header">
                          <WrenchScrewdriverIcon />
                          <span>Corrections</span>
                        </div>
                        <ul className="wn-timeline-list wn-fixes">
                          {release.fixes.map((fix, idx) => (
                            <li key={idx}>{fix}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsNewTab;
